import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Ticket, Menu, FileText, Lock, RotateCcw, HelpCircle, Phone } from 'lucide-react';
import { TermsModal, PrivacyPolicyModal, RefundPolicyModal, FAQModal, ContactModal, AboutModal } from './legal/LegalModals';
import { whatsappUrl } from '../config/whatsapp';
import { goHome } from '../utils/goHome';

/* ─────────────────────────────────────────────────────────────────────────────
   MobileBottomNav — fixed bottom tab bar + "المزيد" sheet, identical on every
   passenger page (mirrors AppNavbar being shared site-wide).

   All 4 items now share one visual system (same icon family/size, same
   active/inactive treatment) — previously الرئيسية was an oversized floating
   blue circle, حجوزاتي used an emoji, المساعدة was WhatsApp-green, and المزيد
   was a bare "☰" character, four different visual languages in one bar.
   المساعدة keeps its WhatsApp green (brand recognition for an external
   action) but is now sized/structured exactly like the other three.
───────────────────────────────────────────────────────────────────────────── */
const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [modal,    setModal]    = useState(null);

  const isHome  = location.pathname === '/';
  const isTrips = location.pathname === '/bookings';

  // Not bus-only icons: سافر is a four-service platform, so the "about"
  // entry uses the actual app logo rather than a transport-specific symbol.
  const moreLinks = [
    { key: 'terms',   Icon: FileText,    label: 'الشروط والأحكام'           },
    { key: 'privacy', Icon: Lock,        label: 'سياسة الخصوصية'            },
    { key: 'refund',  Icon: RotateCcw,   label: 'سياسة الإلغاء والاسترداد'   },
    { key: 'faq',     Icon: HelpCircle,  label: 'الأسئلة الشائعة'            },
    { key: 'contact', Icon: Phone,       label: 'اتصل بنا'                   },
    { key: 'about',   Icon: null,        label: 'حول سافر'                   },
  ];

  const NAV_ITEMS = [
    { key: 'home',  Icon: Home,   label: 'الرئيسية', active: isHome  },
    { key: 'trips', Icon: Ticket, label: 'حجوزاتي',   active: isTrips },
    { key: 'help',  Icon: null,   label: 'المساعدة',  active: false, whatsapp: true },
    { key: 'more',  Icon: Menu,   label: 'المزيد',    active: moreOpen },
  ];

  return (
    <>
      {/* Bottom nav bar — was too subtle against the page's light-blue
          background: a 3px brand-gradient top accent, a darker/stronger
          border+shadow, and higher-contrast inactive icons/text (was
          text-gray-400, quite washed out on white) all make the whole bar
          read as a distinct, solid UI element instead of blending in.
          Active items also get a filled pill background, not just a color
          change, so the current page is unmistakable at a glance. */}
      <nav
        dir="rtl"
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t-2 border-[#1E40AF]/15 shadow-[0_-8px_28px_rgba(15,35,84,.20)] px-2 flex items-center justify-around"
        style={{ paddingTop: '8px', paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
      >
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-l from-[#1E3A8A] via-[#1E40AF] to-[#2748B8]" />
        {NAV_ITEMS.map(item => (
          <button
            key={item.key}
            onClick={() => {
              if (item.key === 'more') { setMoreOpen(true); return; }
              if (item.key === 'help') { window.open(whatsappUrl(), '_blank', 'noopener,noreferrer'); return; }
              // Home needs a real reset, not just navigate('/') — see goHome.
              if (item.key === 'home') { goHome(navigate, location.pathname); return; }
              navigate('/bookings');
            }}
            className={`flex flex-col items-center gap-1 min-w-[56px] min-h-[48px] justify-center rounded-xl px-2 transition-all ${
              item.active
                ? 'text-[#1E40AF] bg-blue-50 font-bold'
                : item.whatsapp ? 'text-green-600' : 'text-[#5B6472] hover:text-[#1E40AF] hover:bg-blue-50/60'
            }`}
          >
            {item.whatsapp ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            ) : (
              <item.Icon size={22} strokeWidth={2} />
            )}
            <span className="text-[11px] font-semibold leading-none">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* "المزيد" bottom sheet */}
      {moreOpen && (
        <div className="fixed inset-0 z-[150] flex items-end" dir="rtl">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => setMoreOpen(false)} />
          <div
            className="relative w-full bg-white rounded-t-[28px] shadow-2xl pb-safe"
            style={{ animation: 'slideDown 0.22s ease both', paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-[17px] font-black text-gray-900">المزيد</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 text-lg font-bold"
              >×</button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              {moreLinks.map(({ key, Icon, label }) => (
                <button
                  key={key}
                  onClick={() => { setModal(key); setMoreOpen(false); }}
                  className="flex items-center gap-3 p-3.5 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 rounded-xl text-sm font-semibold text-gray-700 hover:text-[#1E40AF] transition-all text-right"
                >
                  <span className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {Icon ? <Icon size={16} /> : <img src="/logo.png" alt="" className="w-full h-full object-cover" />}
                  </span>
                  <span className="leading-tight">{label}</span>
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-gray-600 pb-2">جميع الحقوق محفوظة © 2026 سافر</p>
          </div>
        </div>
      )}

      {/* Legal modals opened from sheet */}
      {modal === 'terms'   && <TermsModal         onClose={() => setModal(null)} />}
      {modal === 'privacy' && <PrivacyPolicyModal  onClose={() => setModal(null)} />}
      {modal === 'refund'  && <RefundPolicyModal   onClose={() => setModal(null)} />}
      {modal === 'faq'     && <FAQModal            onClose={() => setModal(null)} />}
      {modal === 'contact' && <ContactModal        onClose={() => setModal(null)} />}
      {modal === 'about'   && <AboutModal          onClose={() => setModal(null)} />}
    </>
  );
};

export default MobileBottomNav;
